import { useState, useEffect, useMemo } from "react";
import { fetchActiveProductKnowledgeJobs, downloadProductKnowledgeData } from "../../apis/productKnowledge";
import { Brands, BrandsFN } from "../../constants/enum";
import { ProductLibProcessJob } from "../../API";
import FolderView, { Product } from "./FolderView";
import TreeView from "./TreeView";
import { ExportPDFButton } from "./components";

// View types
type ViewType = "folder" | "tree";

// Brand logo mapping
const brandLogos: Record<string, string> = {
  FL: "/brands/fineline.png",
  DN: "/brands/deenee.png",
  BE: "/brands/benice.png",
  TS: "/brands/tros.png",
  SM: "/brands/smart.png",
  ES: "/brands/eversense.png",
  TM: "/brands/tomi.png",
  VV: "/brands/vivite.png",
  LT: "/brands/lovlitails.png",
};

// Brand theme colors
const brandThemeColors: Record<string, { bg: string; border: string; text: string }> = {
  FL: { bg: "#FFEEC8", border: "#F5C518", text: "#8B6914" },
  DN: { bg: "#C9DDFF", border: "#5B8DEF", text: "#2E5AAD" },
  BE: { bg: "#FFD0E8", border: "#E6006E", text: "#9C004A" },
  TS: { bg: "#D4D7E3", border: "#4A5278", text: "#2D3348" },
  SM: { bg: "#DCDEE1", border: "#9CA3AF", text: "#4B5563" },
  ES: { bg: "#FFD0E8", border: "#FF69B4", text: "#C4147D" },
  TM: { bg: "#FFF1AC", border: "#F5C518", text: "#8B6914" },
  VV: { bg: "#C8E6D5", border: "#006B31", text: "#004D23" },
  LT: { bg: "#C9DDFF", border: "#5B8DEF", text: "#2E5AAD" },
};

// Create brand options from Brands enum
const brandOptions = Object.entries(Brands).map(([key, value]) => ({
  value: value,
  label: BrandsFN[value as keyof typeof BrandsFN] || key,
}));

// Brand Selection Dialog Component
const BrandSelectionDialog = ({
  isOpen,
  onClose,
  onSelect,
  currentBrand,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (brand: { value: string; label: string }) => void;
  currentBrand: string | null;
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative z-10 w-full max-w-md p-6 mx-4 bg-white rounded-xl shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Select Brand</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 transition-colors rounded-full hover:text-gray-600 hover:bg-gray-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Brand Grid */}
        <div className="grid grid-cols-3 gap-3">
          {brandOptions.map((brand) => {
            const colors = brandThemeColors[brand.value];
            const isSelected = currentBrand === brand.value;

            return (
              <button
                key={brand.value}
                onClick={() => {
                  onSelect(brand);
                  onClose();
                }}
                className={`flex flex-col items-center p-3 rounded-lg transition-all border-2 ${
                  isSelected
                    ? "ring-2 ring-offset-2"
                    : "hover:scale-105"
                }`}
                style={{
                  backgroundColor: colors.bg,
                  borderColor: isSelected ? colors.border : "transparent",
                  ...(isSelected && { "--tw-ring-color": colors.border } as React.CSSProperties),
                }}
              >
                <div className="flex items-center justify-center w-12 h-12 mb-2 bg-white rounded-full shadow">
                  <img
                    src={brandLogos[brand.value]}
                    alt={brand.label}
                    className="object-contain w-8 h-8"
                  />
                </div>
                <span
                  className="text-xs font-medium text-center"
                  style={{ color: colors.text }}
                >
                  {brand.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

function ProductKnowledge() {
  const [activeJob, setActiveJob] = useState<ProductLibProcessJob | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<{ value: string; label: string } | null>(
    brandOptions.find((opt) => opt.value === "FL") || null
  );
  const [knowledgeData, setKnowledgeData] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [viewType, setViewType] = useState<ViewType>("tree");
  const [isBrandDialogOpen, setIsBrandDialogOpen] = useState<boolean>(false);

  const themeColors = brandThemeColors[selectedBrand?.value || "FL"] || brandThemeColors.FL;

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // Fetch active job on component mount
  useEffect(() => {
    const fetchActiveJob = async () => {
      try {
        const jobs = await fetchActiveProductKnowledgeJobs();
        if (jobs && jobs.length > 0) {
          setActiveJob(jobs[0]);
        }
      } catch (err) {
        console.error("Error fetching active jobs:", err);
        setError("Failed to fetch active job");
      }
    };

    fetchActiveJob();
  }, []);

  // Fetch knowledge data when brand is selected and activeJob exists
  useEffect(() => {
    const fetchKnowledgeData = async () => {
      if (!activeJob?.id || !selectedBrand?.value) {
        setKnowledgeData([]);
        return;
      }

      setLoading(true);
      setError(null);
      setSearchTerm("");

      try {
        const data = await downloadProductKnowledgeData(activeJob.id, selectedBrand.value);
        setKnowledgeData(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error downloading knowledge data:", err);
        setError("Failed to download knowledge data");
        setKnowledgeData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchKnowledgeData();
  }, [activeJob, selectedBrand]);

  // Filter products by search term and exclude Obsolete status
  const filteredProducts = useMemo(() => {
    // First, exclude Obsolete (06) products
    const nonObsoleteProducts = knowledgeData.filter((p) => p.status !== "06");

    if (!searchTerm.trim()) return nonObsoleteProducts;

    const term = searchTerm.toLowerCase();
    return nonObsoleteProducts.filter(
      (p) =>
        p.name?.toLowerCase().includes(term) ||
        p.materialCode?.toLowerCase().includes(term) ||
        p.materialName?.toLowerCase().includes(term) ||
        p.barcode?.toLowerCase().includes(term) ||
        p.category?.toLowerCase().includes(term) ||
        p.subcategory?.toLowerCase().includes(term)
    );
  }, [knowledgeData, searchTerm]);

  return (
    <div className="container px-4 py-6 mx-auto">
      <div className="mb-6">
        {/* Title, Active Job ID, and View Type Switch - same row */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Product Knowledge</h1>

          <div className="flex items-center gap-3">
            {/* View Type Switch and Export */}
            {!loading && knowledgeData.length > 0 && (
              <div className="flex items-center gap-3">
                {/* Export PDF Button */}
                <ExportPDFButton
                  products={filteredProducts}
                  themeColors={themeColors}
                  brandName={selectedBrand?.label || "Product Knowledge"}
                />

                {/* View Type Switch */}
                <div className="inline-flex p-1 bg-gray-100 rounded-lg">
                  <button
                    onClick={() => setViewType("tree")}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                      viewType === "tree"
                        ? "bg-white shadow text-gray-900"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
                      />
                    </svg>
                    Tree
                  </button>
                  <button
                    onClick={() => setViewType("folder")}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                      viewType === "folder"
                        ? "bg-white shadow text-gray-900"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                      />
                    </svg>
                    Folder
                  </button>
                </div>
              </div>
            )}

            {/* Active Job ID */}
            {activeJob && (
              <div
                className="flex items-center gap-2 px-3 py-2 rounded-lg"
                style={{ backgroundColor: themeColors.bg }}
              >
                <span className="text-sm" style={{ color: themeColors.text }}>
                  Active Job ID:
                </span>
                <span className="text-sm font-semibold" style={{ color: themeColors.border }}>
                  {activeJob.id?.slice(0, 8)}
                </span>
                <button
                  onClick={() => activeJob.id && copyToClipboard(activeJob.id)}
                  className="p-1 transition-colors rounded hover:bg-white/50"
                  title={copied ? "Copied!" : "Copy full ID"}
                >
                  {copied ? (
                    <svg className="w-4 h-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" style={{ color: themeColors.text }} viewBox="0 0 20 20" fill="currentColor">
                      <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                      <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                    </svg>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Brand Logo - clickable to open dialog */}
        <div className="flex flex-col items-center my-6">
          {selectedBrand && brandLogos[selectedBrand.value] && (
            <button
              onClick={() => setIsBrandDialogOpen(true)}
              disabled={!activeJob}
              className="relative group"
            >
              <div
                className="flex items-center justify-center w-24 h-24 overflow-hidden transition-transform bg-white border-4 rounded-full shadow-lg group-hover:scale-105 group-disabled:opacity-50 group-disabled:cursor-not-allowed"
                style={{ borderColor: themeColors.border }}
              >
                <img
                  src={brandLogos[selectedBrand.value]}
                  alt={selectedBrand.label}
                  className="object-contain h-16 p-2"
                />
              </div>
              {/* Edit indicator */}
              {activeJob && (
                <div
                  className="absolute flex items-center justify-center w-6 h-6 transition-opacity bg-white border-2 rounded-full shadow -right-1 -top-1 group-hover:opacity-100 opacity-70"
                  style={{ borderColor: themeColors.border }}
                >
                  <svg className="w-3 h-3" style={{ color: themeColors.text }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </div>
              )}
              {loading ? (
                <div
                  className="absolute px-3 py-1 transform -translate-x-1/2 rounded-full -bottom-2 left-1/2"
                  style={{ backgroundColor: themeColors.border }}
                >
                  <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                </div>
              ) : (
                filteredProducts.length > 0 && (
                  <div
                    className="absolute px-3 py-1 text-xs font-bold text-center text-white transform -translate-x-1/2 rounded-full -bottom-2 left-1/2"
                    style={{ backgroundColor: themeColors.border }}
                  >
                    {filteredProducts.length.toLocaleString()}
                  </div>
                )
              )}
            </button>
          )}
          {/* Brand name label */}
          {selectedBrand && (
            <span
              className="mt-4 text-sm font-medium"
              style={{ color: themeColors.text }}
            >
              {selectedBrand.label}
            </span>
          )}
        </div>

        {/* Brand Selection Dialog */}
        <BrandSelectionDialog
          isOpen={isBrandDialogOpen}
          onClose={() => setIsBrandDialogOpen(false)}
          onSelect={setSelectedBrand}
          currentBrand={selectedBrand?.value || null}
        />

        {/* Error/Warning States */}
        {!activeJob && (
          <div className="p-4 border border-yellow-200 rounded-lg bg-yellow-50">
            <p className="text-yellow-700">No active Product Knowledge job found.</p>
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center justify-center min-h-[50vh]">
            <div
              className="w-12 h-12 mb-4 border-4 rounded-full border-t-transparent animate-spin"
              style={{ borderColor: themeColors.border, borderTopColor: "transparent" }}
            ></div>
            <span className="font-medium text-gray-600">Loading products...</span>
          </div>
        )}

        {error && (
          <div className="p-4 border border-red-200 rounded-lg bg-red-50">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Main Content - View Components */}
        {!loading && knowledgeData.length > 0 && (
          <>
            {viewType === "tree" ? (
              <TreeView
                products={filteredProducts}
                themeColors={themeColors}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            ) : (
              <FolderView
                products={filteredProducts}
                themeColors={themeColors}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ProductKnowledge;
